/**
 * Error boundary to catch React component crashes and show a friendly error screen.
 */

import React, { Component, type ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { log } from '@/shared/logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    log.error('error_boundary_caught', error, { componentStack: errorInfo.componentStack ?? undefined });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4 p-8">
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="max-w-100 text-center text-sm text-muted-foreground">
            The application encountered an unexpected error. Please try reloading the page.
          </p>
          <Button onClick={this.handleReload}>Reload Page</Button>
        </div>
      );
    }

    return this.props.children;
  }
}
